import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faArrowRight, faCheck, faShieldHalved, faChartLine, 
  faGraduationCap, faHandshake, faUsers, faLightbulb,
  faArrowLeft
} from "@fortawesome/free-solid-svg-icons";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import useScrollReveal from "../hooks/useScrollReveal";
import "../Styles/ServiceDetail.css";

const servicesData = {
  "life-insurance": {
    icon: faShieldHalved,
    title: "Life Insurance & Protection",
    subtitle: "Comprehensive coverage designed to protect your family's financial future.",
    heroDesc: "Life insurance is the foundation of any financial plan. It provides the safety net your family needs to maintain their lifestyle, pay off debts, and achieve their dreams — even if the unexpected happens.",
    features: [
      { title: "Term Life Insurance", desc: "Affordable coverage for a specific period. Ideal for young families with mortgages, student loans, or temporary financial obligations." },
      { title: "Whole Life Insurance", desc: "Permanent coverage with guaranteed cash value growth. A wealth-building tool that provides lifelong protection." },
      { title: "Universal Life Insurance", desc: "Flexible premiums and death benefits with cash value accumulation tied to market performance." },
      { title: "Income Protection", desc: "Disability and income replacement coverage to ensure your family's standard of living is maintained if you can't work." },
    ],
    benefits: [
      "Tax-free death benefit for your beneficiaries",
      "Cash value growth on permanent policies",
      "Living benefits for critical and chronic illness",
      "Flexible coverage amounts and terms",
      "Policy loans available on whole life",
      "Estate planning and wealth transfer tool"
    ],
    whoIsItFor: "Life insurance is essential for anyone with financial dependents — whether you're a young parent, a business owner, or someone planning their legacy. If someone depends on your income, you need life insurance.",
    cta: "Find the right coverage for your family. Schedule a free policy review today."
  },
  "retirement-planning": {
    icon: faChartLine,
    title: "Retirement Planning",
    subtitle: "Strategic roadmaps for financial independence at every stage of life.",
    heroDesc: "Retirement planning isn't just about saving — it's about creating a comprehensive strategy that ensures you can live the life you want, on your terms, for as long as you need.",
    features: [
      { title: "IRA Optimization", desc: "Traditional, Roth, and SEP IRA strategies to maximize your tax advantages and growth potential." },
      { title: "401(k) Strategy", desc: "Employer plan optimization, contribution strategies, and Roth conversion planning for maximum returns." },
      { title: "Pension Planning", desc: "Analysis of pension options, lump-sum vs. annuity decisions, and supplemental income strategies." },
      { title: "Social Security Maximization", desc: "Timing strategies and claiming decisions that can add tens of thousands to your lifetime benefits." },
    ],
    benefits: [
      "Personalized retirement income projections",
      "Tax-efficient withdrawal strategies",
      "Healthcare cost planning for retirement",
      "Multiple income stream creation",
      "Inflation protection strategies",
      "Legacy planning integration"
    ],
    whoIsItFor: "Whether you're 25 or 55, retirement planning is for you. The earlier you start, the more powerful compound growth works in your favor. But it's never too late to optimize your strategy.",
    cta: "Let's design your retirement roadmap. Book your complimentary planning session."
  },
  "financial-education": {
    icon: faGraduationCap,
    title: "Financial Education",
    subtitle: "Transforming how families think about and manage money.",
    heroDesc: "Financial literacy is the most powerful tool we can give families. Our Real Secrets of Money program provides the knowledge and confidence to make smart financial decisions for life.",
    features: [
      { title: "Budgeting Mastery", desc: "Learn proven systems to manage cash flow, eliminate waste, and direct every dollar toward your goals." },
      { title: "Debt Elimination", desc: "Strategic debt payoff methods that accelerate your path to financial freedom — not just minimum payments." },
      { title: "Investment Basics", desc: "Understand how markets work, risk vs. reward, and how to build a diversified portfolio aligned with your goals." },
      { title: "Estate Planning 101", desc: "Learn the fundamentals of wills, trusts, and beneficiary designations to protect your family's legacy." },
    ],
    benefits: [
      "Interactive workshops and seminars",
      "One-on-one financial coaching sessions",
      "Real Secrets of Money curriculum",
      "Practical tools and resources",
      "Family financial planning sessions",
      "Ongoing education and updates"
    ],
    whoIsItFor: "Our education programs serve everyone — from young adults just starting their financial journey to established families looking to optimize their wealth strategy.",
    cta: "Ready to transform your financial knowledge? Join our next educational workshop."
  },
  "business-protection": {
    icon: faHandshake,
    title: "Business Protection",
    subtitle: "Safeguarding your business, your partners, and your employees.",
    heroDesc: "Your business is more than a livelihood — it's a legacy. Business protection ensures continuity, protects key relationships, and provides the security your team deserves.",
    features: [
      { title: "Key Person Insurance", desc: "Protect your business from the financial impact of losing essential team members. Coverage designed for critical roles." },
      { title: "Buy-Sell Agreements", desc: "Funded agreements that ensure smooth ownership transitions, protect surviving partners, and provide fair value to departing owners." },
      { title: "Business Succession Planning", desc: "Comprehensive exit strategies and transition plans that preserve your business value and legacy." },
      { title: "Group Benefits", desc: "Competitive employee benefits packages that attract talent, boost retention, and demonstrate your commitment to your team." },
    ],
    benefits: [
      "Business continuity planning",
      "Tax-advantaged funding strategies",
      "Employee retention solutions",
      "Partnership protection",
      "Executive benefits planning",
      "Business valuation guidance"
    ],
    whoIsItFor: "Business protection is essential for any business owner — from solo practitioners to multi-partner firms. If your business has employees, partners, or significant revenue, you need a protection strategy.",
    cta: "Protect your business investment. Schedule a business protection assessment."
  },
  "legacy-planning": {
    icon: faUsers,
    title: "Legacy Planning",
    subtitle: "Multi-generational wealth strategies built to last.",
    heroDesc: "True wealth isn't just what you accumulate — it's what you pass on. Legacy planning ensures your values, wealth, and wishes are preserved for generations to come.",
    features: [
      { title: "Estate Planning Integration", desc: "Coordinate your financial plan with estate documents — wills, trusts, and powers of attorney — for comprehensive protection." },
      { title: "Wealth Transfer Strategies", desc: "Tax-efficient methods to transfer assets to the next generation while minimizing estate taxes and probate." },
      { title: "Trust Planning", desc: "Revocable and irrevocable trust strategies for asset protection, tax minimization, and controlled wealth distribution." },
      { title: "Charitable Giving", desc: "Strategies that align your philanthropic goals with tax benefits — creating impact while preserving family wealth." },
    ],
    benefits: [
      "Multi-generational wealth preservation",
      "Estate tax minimization strategies",
      "Asset protection from creditors",
      "Controlled wealth distribution",
      "Family governance frameworks",
      "Values-based wealth transfer"
    ],
    whoIsItFor: "Legacy planning is for anyone who wants their wealth and values to outlast them. Whether you're building an inheritance for your children or establishing a charitable foundation.",
    cta: "Start building your family's legacy today. Schedule a legacy planning consultation."
  },
  "wealth-accumulation": {
    icon: faLightbulb,
    title: "Wealth Accumulation",
    subtitle: "Tax-efficient growth strategies for high-income earners.",
    heroDesc: "Building wealth isn't just about earning more — it's about keeping more of what you earn and growing it efficiently. Our strategies are designed for those ready to accelerate their financial growth.",
    features: [
      { title: "Kai-Zen Plan", desc: "Our exclusive supplemental retirement strategy that leverages institutional financing to help you accumulate significantly more than traditional methods allow." },
      { title: "Index Universal Life (IUL)", desc: "Market-linked growth with downside protection. Accumulate cash value with the potential for double-digit returns and zero market-loss risk." },
      { title: "Tax-Free Growth Strategies", desc: "Legally minimize your tax burden while maximizing growth through Roth conversions, IUL, and other tax-advantaged vehicles." },
      { title: "Supplemental Retirement Income", desc: "Create additional income streams beyond 401(k) and Social Security — designed to maintain your lifestyle in retirement." },
    ],
    benefits: [
      "Tax-free retirement income potential",
      "Market-linked growth with downside protection",
      "Institutional-grade financing strategies",
      "No contribution limits (unlike 401k/IRA)",
      "Living benefits and death benefit",
      "Asset protection in many states"
    ],
    whoIsItFor: "Wealth accumulation strategies are ideal for high-income earners ($150K+) who have maximized traditional retirement accounts and want additional tax-efficient growth opportunities.",
    cta: "Discover how much more you could be accumulating. Book your wealth strategy session."
  }
};

function ServiceDetail() {
  useScrollReveal();
  const { slug } = useParams();
  const service = servicesData[slug];

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const serviceKeys = Object.keys(servicesData);
  const currentIndex = serviceKeys.indexOf(slug);
  const nextService = serviceKeys[(currentIndex + 1) % serviceKeys.length];
  const prevService = serviceKeys[(currentIndex - 1 + serviceKeys.length) % serviceKeys.length];

  return (
    <div className="service-detail-page">
      <Navbar />

      {/* Hero */}
      <section className="sd-hero">
        <div className="sd-hero-bg">
          <div className="sd-hero-grid"></div>
          <div className="sd-hero-orb"></div>
          <div className="sd-hero-particles">
            <span className="sd-p" style={{top:'20%',left:'10%',animationDelay:'0s'}}></span>
            <span className="sd-p" style={{top:'60%',left:'80%',animationDelay:'2s'}}></span>
            <span className="sd-p" style={{top:'30%',right:'15%',animationDelay:'4s'}}></span>
            <span className="sd-p" style={{bottom:'25%',left:'30%',animationDelay:'6s'}}></span>
          </div>
        </div>
        <div className="container sd-hero-content">
          <Link to="/services" className="sd-back-link">
            <FontAwesomeIcon icon={faArrowLeft} /> All Services
          </Link>
          <div className="sd-hero-icon">
            <FontAwesomeIcon icon={service.icon} />
          </div>
          <h1>{service.title}</h1>
          <p className="sd-hero-subtitle">{service.subtitle}</p>
        </div>
      </section>

      {/* Overview */}
      <section className="sd-overview section-padding reveal">
        <div className="container">
          <div className="sd-overview-grid">
            <div className="sd-overview-left">
              <span className="section-tag">Overview</span>
              <h2 className="section-title">What You Need To Know</h2>
              <p className="sd-overview-text">{service.heroDesc}</p>
              <p className="sd-overview-who"><strong>Who is this for?</strong> {service.whoIsItFor}</p>
            </div>
            <div className="sd-overview-right">
              <div className="sd-benefits-card">
                <h4>Key Benefits</h4>
                <ul>
                  {service.benefits.map((b, i) => (
                    <li key={i}>
                      <FontAwesomeIcon icon={faCheck} className="sd-check" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="sd-features section-padding reveal">
        <div className="container">
          <div className="sd-features-header">
            <span className="section-tag">What's Included</span>
            <h2 className="section-title">Our {service.title} Solutions</h2>
          </div>
          <div className="sd-features-grid">
            {service.features.map((f, i) => (
              <div className="sd-feature-card" key={i}>
                <div className="sd-feature-num">0{i + 1}</div>
                <h4>{f.title}</h4>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="sd-cta section-padding reveal">
        <div className="container">
          <div className="sd-cta-box">
            <h2>{service.cta}</h2>
            <Link to="/schedule-appointment" className="btn-primary">
              Schedule Free Consultation <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="sd-nav">
        <div className="container sd-nav-inner">
          <Link to={`/services/${prevService}`} className="sd-nav-link sd-nav-prev">
            <span className="sd-nav-label"><FontAwesomeIcon icon={faArrowLeft} /> Previous</span>
            <span className="sd-nav-title">{servicesData[prevService].title}</span>
          </Link>
          <Link to={`/services/${nextService}`} className="sd-nav-link sd-nav-next">
            <span className="sd-nav-label">Next <FontAwesomeIcon icon={faArrowRight} /></span>
            <span className="sd-nav-title">{servicesData[nextService].title}</span>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default ServiceDetail;
